/**
 * Tracing (engine target §5): a ray goes from where it is to the NEAREST surface
 * it meets, that surface's rule turns it, and it carries on. Nothing is stepped.
 *
 * One ray in, one ray out: refraction gives the refracted ray only, and total
 * internal reflection gives the reflected one only. Partial reflection is not
 * modelled, and that is an idealisation, not an oversight.
 */
import { Surface, Vec2, Vertex, Ray } from './types';
import { add, angleBetween, cross, deg, dot, neg, perp, scale, sub, unit } from './vec';

/** Two facing mirrors reflect for ever; this is where a trace stops and says so. */
export const BOUNCE_CAP = 64;
/** A ray leaving a surface must travel at least this far before it can hit one. */
const EPS = 1e-7;
/** How far a ray that hits nothing is carried, cm — past anything on the bench. */
export const REACH = 5000;

interface Hit {
    t: number;
    at: Vec2;
    /** The geometric normal: outward for a segment of a CCW outline, radial for an arc. */
    normal: Vec2;
    surface: Surface;
    outsideAperture: boolean;
}

export interface TraceOptions {
    /** A probe passes through `probeTransparent` surfaces (screens). */
    probe?: boolean;
    /** Ideal surfaces treated as infinite lines — a construction ray is bent by
     *  the element even where the element is not. */
    unbounded?: ReadonlySet<string>;
    /** nm: the index of a dispersing material is read at this wavelength. */
    wavelength?: number;
}

/** The wavelength every index on the bench is quoted at: sodium yellow. */
export const REFERENCE_WAVELENGTH = 589;

/** A material's index at a wavelength — Cauchy's n(λ) = n₅₈₉ + B(1/λ² − 1/589²).
 *  Without a `dispersion` B, the same index for every colour. */
export function indexAt(n589: number, dispersion: number | undefined, nm: number | undefined): number {
    if (!dispersion || !nm) return n589;
    return n589 + dispersion * (1 / (nm * nm) - 1 / (REFERENCE_WAVELENGTH * REFERENCE_WAVELENGTH));
}

function hitSurface(p: Vec2, d: Vec2, s: Surface, unbounded: boolean): Hit | null {
    const shape = s.shape;
    if (shape.form === 'segment') {
        const e = sub(shape.b, shape.a);
        const denom = cross(d, e);
        if (Math.abs(denom) < 1e-15) return null;
        const w = sub(shape.a, p);
        const t = cross(w, e) / denom;
        const u = cross(w, d) / denom;
        if (t <= EPS) return null;
        const outside = u < 0 || u > 1;
        if (outside && !unbounded) return null;
        return {
            t, at: add(p, scale(d, t)), surface: s, outsideAperture: outside,
            normal: unit({ x: e.y, y: -e.x }),
        };
    }
    const f = sub(p, shape.centre);
    const b = dot(f, d);
    const c = dot(f, f) - shape.radius * shape.radius;
    const disc = b * b - c;
    if (disc < 0) return null;
    const r = Math.sqrt(disc);
    for (const t of [-b - r, -b + r]) {
        if (t <= EPS) continue;
        const at = add(p, scale(d, t));
        if (!onArc(Math.atan2(at.y - shape.centre.y, at.x - shape.centre.x), shape.from, shape.to)) continue;
        return { t, at, surface: s, outsideAperture: false, normal: unit(sub(at, shape.centre)) };
    }
    return null;
}

const TAU = 2 * Math.PI;
const wrap = (a: number): number => ((a % TAU) + TAU) % TAU;
const onArc = (a: number, from: number, to: number): boolean =>
    wrap(a - from) <= wrap(to - from) + 1e-12;

function nearest(p: Vec2, d: Vec2, surfaces: Surface[], opts: TraceOptions): Hit | null {
    let best: Hit | null = null;
    for (const s of surfaces) {
        if (opts.probe && s.probeTransparent) continue;
        const h = hitSurface(p, d, s, !!opts.unbounded?.has(s.id));
        if (!h) continue;
        if (!best || h.t < best.t - 1e-12 || (Math.abs(h.t - best.t) <= 1e-12 && h.surface.id < best.surface.id)) {
            best = h;
        }
    }
    return best;
}

type Turn = { next: Vec2 | null } & Partial<Pick<Vertex, 'incidence' | 'refraction' | 'n1' | 'n2' | 'tir'>>;

/** What the surface's rule does to a ray arriving along `d`. `next: null` absorbs it. */
function turn(h: Hit, d: Vec2, nm?: number): Turn {
    const rule = h.surface.rule;
    switch (rule.law) {
        case 'absorb':
            return { next: null };
        case 'reflect': {
            const k = dot(d, h.normal);
            const i = deg(Math.acos(Math.min(1, Math.abs(k))));
            return { next: unit(sub(d, scale(h.normal, 2 * k))), incidence: i, refraction: i };
        }
        case 'refract': {
            let cosi = -dot(d, h.normal);
            const inside = indexAt(rule.inside, rule.dispersion, nm);
            let n1 = rule.outside, n2 = inside, nn = h.normal;
            if (cosi < 0) {
                cosi = -cosi; n1 = inside; n2 = rule.outside; nn = neg(h.normal);
            }
            const eta = n1 / n2;
            const sin2t = eta * eta * (1 - cosi * cosi);
            const incidence = deg(Math.acos(Math.min(1, cosi)));
            if (sin2t > 1) {
                return { next: unit(add(d, scale(nn, 2 * cosi))), incidence, n1, n2, tir: true };
            }
            const cost = Math.sqrt(1 - sin2t);
            return {
                next: unit(add(scale(d, eta), scale(nn, eta * cosi - cost))),
                incidence, refraction: deg(Math.asin(Math.sqrt(sin2t))), n1, n2, tir: false,
            };
        }
        case 'ideal': {
            // A mirror reflects only on the side its axis points to.
            if (rule.folds && dot(d, rule.axis) > 0) return { next: null };
            const along = dot(d, rule.axis) >= 0 ? rule.axis : neg(rule.axis);
            const da = dot(d, along);
            if (da < 1e-12) return { next: d };
            const n = perp(along);
            const y = dot(sub(h.at, rule.centre), n);
            const slope = dot(d, n) / da - (Number.isFinite(rule.f) ? y / rule.f : 0);
            const next = unit(add(rule.folds ? neg(along) : along, scale(n, slope)));
            if (!Number.isFinite(rule.f)) {
                // A plane mirror is an exact reflection, so its angles are real ones.
                const i = deg(Math.acos(Math.min(1, da)));
                return { next, incidence: i, refraction: i };
            }
            return { next };
        }
    }
}

/** Trace one ray from `from` along `direction` through every surface on the bench. */
export function traceRay(
    surfaces: Surface[], from: Vec2, direction: Vec2, opts: TraceOptions = {},
): Pick<Ray, 'vertices' | 'ended'> {
    let p = from;
    let d = unit(direction);
    const vertices: Vertex[] = [{ at: p, direction: d, deviation: 0 }];
    for (let bounce = 0; bounce < BOUNCE_CAP; bounce++) {
        const h = nearest(p, d, surfaces, opts);
        if (!h) {
            vertices.push({ at: add(p, scale(d, REACH)), direction: d, deviation: 0 });
            return { vertices, ended: 'left-the-bench' };
        }
        const { next, ...angles } = turn(h, d, opts.wavelength);
        const base = {
            at: h.at, surfaceId: h.surface.id, partId: h.surface.partId, ...angles,
            ...(h.outsideAperture ? { outsideAperture: true } : {}),
        };
        if (!next) {
            vertices.push({ ...base, direction: d, deviation: 0 });
            return { vertices, ended: 'absorbed' };
        }
        vertices.push({ ...base, direction: next, deviation: angleBetween(d, next) });
        p = h.at;
        d = next;
    }
    return { vertices, ended: 'bounce-cap' };
}
